import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type {
  ExamRecord,
  ExamSchedule,
  ExamScore,
  LessonLog,
  MakeupSchedule,
  ParentSharedStudent,
  ProgressItem,
  TestRecord,
} from '@/types';
import ParentStudentCard from '@/pages/share/components/ParentStudentCard';
import ParentLessonSection from '@/pages/share/components/ParentLessonSection';
import ParentProgressSection from '@/pages/share/components/ParentProgressSection';
import ParentExamRecordSection from '@/pages/share/components/ParentExamRecordSection';
import ParentExamScoreSection from '@/pages/share/components/ParentExamScoreSection';
import ParentExamScheduleSection from '@/pages/share/components/ParentExamScheduleSection';
import ParentTuitionCard from '@/pages/share/components/ParentTuitionCard';
import ParentMakeupSection from '@/pages/share/components/ParentMakeupSection';

interface ShareResponse {
  ok: boolean;
  student?: ParentSharedStudent;
  lessons?: LessonLog[];
  tests?: TestRecord[];
  progress?: ProgressItem[];
  examRecords?: ExamRecord[];
  examSchedules?: ExamSchedule[];
  examScores?: ExamScore[];
  makeupSchedules?: MakeupSchedule[];
}

export default function ParentSharePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [student, setStudent] = useState<ParentSharedStudent | null>(null);
  const [lessons, setLessons] = useState<LessonLog[]>([]);
  const [tests, setTests] = useState<TestRecord[]>([]);
  const [progress, setProgress] = useState<ProgressItem[]>([]);
  const [examRecords, setExamRecords] = useState<ExamRecord[]>([]);
  const [examSchedules, setExamSchedules] = useState<ExamSchedule[]>([]);
  const [examScores, setExamScores] = useState<ExamScore[]>([]);
  const [makeupSchedules, setMakeupSchedules] = useState<MakeupSchedule[]>([]);

  const load = useCallback(async () => {
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setInvalid(false);
    try {
      const { data, error } = await supabase.functions.invoke('parent-share', {
        body: { token },
      });
      if (error) throw error;
      const res = data as ShareResponse | null;
      if (!res?.ok || !res.student) {
        setInvalid(true);
        return;
      }
      setStudent(res.student);
      setLessons(res.lessons ?? []);
      setTests(res.tests ?? []);
      setProgress(res.progress ?? []);
      setExamRecords(res.examRecords ?? []);
      setExamSchedules(res.examSchedules ?? []);
      setExamScores(res.examScores ?? []);
      setMakeupSchedules(res.makeupSchedules ?? []);
    } catch {
      setInvalid(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const testsByLesson = useMemo(() => {
    const map: Record<string, TestRecord[]> = {};
    tests.forEach((t) => {
      if (!t.lesson_log_id) return;
      if (!map[t.lesson_log_id]) map[t.lesson_log_id] = [];
      map[t.lesson_log_id].push(t);
    });
    return map;
  }, [tests]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
        불러오는 중...
      </div>
    );
  }

  if (invalid || !student) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background-50 px-4 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-link-unlink-m text-2xl"></i>
        </span>
        <h1 className="text-lg font-bold text-foreground-950">공유 링크를 사용할 수 없어요</h1>
        <p className="max-w-sm text-sm text-foreground-600">
          링크가 만료되었거나 공유가 중지되었을 수 있어요. 선생님께 새 링크를 요청해주세요.
        </p>
        <Link
          to="/"
          className="mt-1 text-sm font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap"
        >
          홈으로 돌아가기
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <header className="sticky top-0 z-10 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-graduation-cap-line"></i>
            </span>
            <div>
              <p className="text-sm font-bold text-foreground-950">{student.name} 학습 리포트</p>
              <p className="text-[11px] text-foreground-500">학부모님 열람용</p>
            </div>
          </div>
          <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-900">
            <i className="ri-shield-check-line"></i>
            안전하게 공유됨
          </span>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 md:px-6 md:py-8">
        <ParentStudentCard student={student} />

        <ParentProgressSection progress={progress} />

        <ParentExamScheduleSection schedules={examSchedules} />

        <ParentExamRecordSection records={examRecords} />

        <ParentExamScoreSection scores={examScores} />

        <ParentLessonSection lessons={lessons} testsByLesson={testsByLesson} />

        <ParentMakeupSection schedules={makeupSchedules} lessons={lessons} />

        <ParentTuitionCard student={student} />

        <p className="border-t border-background-200 pt-4 text-center text-[11px] text-foreground-400">
          본 페이지는 선생님이 공유한 학습 리포트입니다. 문의는 담당 선생님께 해주세요.
        </p>
      </main>
    </div>
  );
}